import React from "react";
import Footer from "../FooterBn";

/**
 * Goat Teachers — Copyright Compliance & Intellectual Property Policy
 * Tailwind-only (no CSS), matching the same UI style as your other legal pages.
 */

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
    {children}
  </span>
);

const Card = ({ children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    {children}
  </div>
);

const Section = ({ id, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="mb-3">
      <h2 className="text-lg font-black text-slate-900 sm:text-xl">{title}</h2>
      {subtitle ? (
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          {subtitle}
        </p>
      ) : null}
    </div>
    <Card>{children}</Card>
  </section>
);

const RuleList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
        <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-slate-300" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const InfoBox = ({ title, children, tone = "neutral" }) => {
  const tones = {
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    danger: "border-rose-200 bg-rose-50 text-rose-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return (
    <div
      className={`rounded-2xl border p-4 text-sm leading-relaxed ${tones[tone]}`}
    >
      <div className="mb-1 font-extrabold">{title}</div>
      <div>{children}</div>
    </div>
  );
};

const CopyrightIPPolicy = () => {
  const effectiveDate = "20-12-2025";
  const lastUpdated = "20-12-2025";

  const navItems = [
    ["responsibility", "1. User Responsibility"],
    ["prohibited", "2. Prohibited Copyright Infringement"],
    ["platform-ownership", "3. Ownership of Platform Content"],
    ["reporting", "4. Reporting Copyright Infringement"],
    ["takedown", "5. Takedown Procedure"],
    ["counter", "6. Counter-Notification"],
    ["repeat", "7. Repeat Infringers"],
    ["fairuse", "8. Educational Fair Use Disclaimer"],
    ["liability", "9. Limitation of Liability"],
    ["mods", "10. Policy Modifications"],
    ["law", "11. Governing Law"],
    ["contact", "12. Contact Information"],
  ];

  return (
    <>
      <div className="relative isolate bg-slate-50">
        {/* subtle background blobs */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-[-120px] h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-slate-200/50 blur-3xl" />
          <div className="absolute right-[-120px] top-[180px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
          <div className="absolute left-[-120px] top-[420px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          {/* Header */}
          <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 flex flex-wrap gap-2">
                  <Chip>Legal</Chip>
                  <Chip>Copyright</Chip>
                  <Chip>Intellectual Property</Chip>
                </div>

                <h1 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  Goat Teachers – Copyright Compliance &amp; Intellectual
                  Property Policy
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                  Goat Teachers (“we,” “us,” or “our”) respects the intellectual
                  property rights of others and expects all users of the Goat
                  Teachers platform (“Platform”) to do the same. This Policy
                  outlines our approach to copyright protection, infringement
                  reporting, and content removal.
                </p>

                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Effective Date:{" "}
                    <span className="font-bold text-slate-900">
                      {effectiveDate}
                    </span>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Last Updated:{" "}
                    <span className="font-bold text-slate-900">
                      {lastUpdated}
                    </span>
                  </span>
                </div>
              </div>

              {/* Quick Nav */}
              <div className="w-full lg:max-w-xs">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Quick Navigation
                  </div>
                  <div className="mt-3 grid gap-2 text-sm">
                    {navItems.map(([id, label]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 shadow-sm hover:border-slate-300"
                      >
                        <span className="font-semibold">{label}</span>
                        <span className="text-slate-400 group-hover:text-slate-700">
                          →
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-3 lg:grid-cols-2">
              <InfoBox
                title="Zero tolerance for repeated infringement"
                tone="danger"
              >
                Repeated or intentional copyright infringement may result in
                permanent account termination.
              </InfoBox>
              <InfoBox title="We are a hosting platform" tone="neutral">
                Goat Teachers acts as a hosting service for user-generated
                content and does not actively monitor every upload.
              </InfoBox>
            </div>
          </div>

          {/* Sections */}
          <div className="grid gap-6">
            <Section id="responsibility" title="1. User Responsibility">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>
                  Users are solely responsible for ensuring that any content
                  they upload, post, share, or distribute on the Platform
                  complies with applicable copyright, trademark, and
                  intellectual property laws.
                </p>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    By submitting content, you represent and warrant that:
                  </div>
                  <div className="mt-2">
                    <RuleList
                      items={[
                        "You own the content or have lawful permission to use and share it",
                        "Your content does not infringe upon the rights of any third party",
                        "You have the authority to grant Goat Teachers the rights described in our Terms & Conditions",
                      ]}
                    />
                  </div>
                </div>
              </div>
            </Section>

            <Section
              id="prohibited"
              title="2. Prohibited Copyright Infringement"
            >
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>Users must not upload or share:</p>
                <RuleList
                  items={[
                    "Copyrighted textbooks, books, paid course materials, or exam papers without authorization",
                    "Pirated notes, guides, or question banks",
                    "Content copied from websites, apps, or instructors without permission",
                    "Materials that violate licensing terms or institutional policies",
                  ]}
                />
                <InfoBox title="Enforcement" tone="danger">
                  Repeated or intentional infringement may result in permanent
                  account termination.
                </InfoBox>
              </div>
            </Section>

            <Section
              id="platform-ownership"
              title="3. Ownership of Platform Content"
            >
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>All platform-owned content, including but not limited to:</p>
                <RuleList
                  items={[
                    "Website design and layout",
                    "Logos, trademarks, and branding",
                    "Software, databases, and algorithms",
                  ]}
                />
                <InfoBox title="Restrictions" tone="warn">
                  These are the exclusive intellectual property of Goat Teachers
                  and may not be copied, modified, or used without written
                  permission.
                </InfoBox>
              </div>
            </Section>

            <Section
              id="reporting"
              title="4. Reporting Copyright Infringement"
              subtitle="If you believe content on Goat Teachers infringes your copyright, submit a written notice."
            >
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <RuleList
                  items={[
                    "Identification of the copyrighted work claimed to be infringed",
                    "Identification of the allegedly infringing content and its location on the Platform",
                    "Your full name, email address, and contact details",
                    "A statement that you have a good-faith belief the use is unauthorized",
                    "A statement that the information provided is accurate and, under penalty of perjury, you are the copyright owner or authorized to act on their behalf",
                  ]}
                />

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Send copyright notices to:
                  </div>
                  <div className="mt-2">
                    <div className="text-sm text-slate-700">
                      Email:{" "}
                      <span className="font-bold text-slate-900">
                        support@goatteachers.com
                      </span>
                    </div>
                  </div>
                </div>

                <InfoBox title="Tip" tone="neutral">
                  For faster processing, include direct links/screenshots and
                  any supporting proof of ownership.
                </InfoBox>
              </div>
            </Section>

            <Section id="takedown" title="5. Takedown Procedure">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>Upon receipt of a valid infringement notice:</p>
                <RuleList
                  items={[
                    "We will review the complaint",
                    "The allegedly infringing content may be removed or disabled",
                    "The uploader may be notified of the takedown",
                  ]}
                />
                <InfoBox title="No prior notice when necessary" tone="warn">
                  Goat Teachers reserves the right to remove content without
                  prior notice where necessary to comply with legal obligations
                  or protect platform integrity.
                </InfoBox>
              </div>
            </Section>

            <Section id="counter" title="6. Counter-Notification">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>
                  If you believe your content was removed in error, you may
                  submit a counter-notification including:
                </p>
                <RuleList
                  items={[
                    "Identification of the removed content",
                    "A statement under penalty of perjury that the content was removed due to mistake or misidentification",
                    "Your consent to jurisdiction under applicable law",
                  ]}
                />
                <InfoBox title="Restoration" tone="neutral">
                  Upon receipt of a valid counter-notification, content may be
                  restored at our discretion unless legal action is initiated by
                  the copyright owner.
                </InfoBox>
              </div>
            </Section>

            <Section id="repeat" title="7. Repeat Infringers">
              <InfoBox title="Repeat infringer policy" tone="danger">
                Goat Teachers maintains a policy of terminating accounts of
                users who are determined to be repeat copyright infringers.
              </InfoBox>
            </Section>

            <Section id="fairuse" title="8. Educational Fair Use Disclaimer">
              <InfoBox title="Fair use note" tone="warn">
                Some content may qualify as “fair use” under applicable
                copyright laws for educational purposes. However, Goat Teachers
                does not guarantee that any content qualifies as fair use and
                assumes no liability for user-uploaded materials.
              </InfoBox>
            </Section>

            <Section id="liability" title="9. Limitation of Liability">
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>
                  Goat Teachers acts solely as a hosting service for
                  user-generated content and does not actively monitor all
                  uploads. To the maximum extent permitted by law, Goat Teachers
                  shall not be liable for:
                </p>
                <RuleList
                  items={[
                    "User-generated copyright infringement",
                    "Damages arising from content removal or account termination",
                    "Loss of access to content due to compliance actions",
                  ]}
                />
              </div>
            </Section>

            <Section id="mods" title="10. Policy Modifications">
              <div className="text-sm leading-relaxed text-slate-700">
                We reserve the right to modify this Copyright Compliance Policy
                at any time. Changes will be effective upon posting on the
                Platform.
              </div>
            </Section>

            <Section id="law" title="11. Governing Law">
              <div className="text-sm leading-relaxed text-slate-700">
                This Policy shall be governed by and interpreted in accordance
                with the laws of the People’s Republic of Bangladesh.
              </div>
            </Section>

            <Section id="contact" title="12. Contact Information">
              <div className="space-y-3 text-sm leading-relaxed text-slate-700">
                <p>For copyright-related inquiries:</p>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Goat Teachers
                  </div>
                  <div className="mt-2 space-y-1">
                    <div>
                      Email:{" "}
                      <a
                        href="mailto:support@goatteachers.com"
                        className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                      >
                        support@goatteachers.com
                      </a>
                    </div>
                    <div>
                      Website:{" "}
                      <a
                        href="https://www.goatteachers.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                      >
                        https://www.goatteachers.com
                      </a>
                    </div>
                  </div>
                </div>

                <InfoBox title="Reminder" tone="neutral">
                  If you’re reporting infringement, include the exact content
                  location (URL/post ID) and the required statements listed in
                  section 4.
                </InfoBox>
              </div>
            </Section>

            {/* Bottom reminder */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
              <div className="font-extrabold text-slate-900">Reminder</div>
              <p className="mt-1 leading-relaxed">
                By continuing to use Goat Teachers, you acknowledge that you
                have read and agree to this Copyright Compliance &amp;
                Intellectual Property Policy.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default CopyrightIPPolicy;
